import React from 'react';
import styled from "styled-components";
import {BREAKPOINTS, COLORS, Quote} from "../styles";
import {IconButton} from "../Button";
import {SystemIcons} from "../icons";
import {LinearProgress, LinearProgressType} from "../LinearProgress";
import {useMediaMatch} from "rooks";
import {Size} from "../types";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${props => COLORS.getColor('neutral_20', props.theme)};
  min-height: 100%;
  align-items: flex-end;
  box-sizing: border-box;
  overflow: hidden;

  gap: 24px;
  padding: 24px 32px 64px;

  ${BREAKPOINTS.LARGE} {
    gap: 32px;
    padding: 32px 64px 128px;
  }

  &.reverse {
    align-items: flex-start;
  }
`;

const Media = styled.div`
  display: flex;
  width: 320px;
  height: 200px;


  img {
    object-fit: contain;
  }

  img.fit {
    object-fit: contain;
  }

  img.fill {
    object-fit: cover;
  }

  max-width: 100%;

  img {
    max-width: 100%;
    width: 320px;
    height: 200px;
  }

  ${BREAKPOINTS.LARGE} {
    width: 488px;
    height: 280px;

    img {
      width: 488px;
      height: 280px;
    }
  }

`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  width: 100%;
  max-width: 320px;

  ${BREAKPOINTS.LARGE} {
    max-width: 488px;
  }
  
  p:not(:first-of-type) {
    flex: 1;
    color: ${props => COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme)};
  }


`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  max-width: 320px;

  ${BREAKPOINTS.LARGE} {
    max-width: 488px;
  }

  .progress {
    width: max-content;
  }
`;

export interface BasePanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'content'> {
  type?: 'default' | 'carousel';
}

export interface PanelContent {
  media?: { type: 'image', src: string; alt: string, fit?: 'fill' | 'fit' } | { type: 'custom', content: React.ReactNode };
  title?: string;
  content?: React.ReactNode;
}

export interface DefaultPanelProps extends BasePanelProps, PanelContent {
  /**
   * Optional. Specifies the type of the panel as 'default'.
   * If not specified, the type defaults to 'default'.
   */
  type?: 'default';

  /**
   * Optional. Specifies the order of the content in the panel.
   * It can be 'default' or 'reverse'. Defaults to 'default' if not specified.
   */
  order?: 'default' | 'reverse';
}

export interface CarouselPanelProps extends BasePanelProps {
  /**
   * Required. Specifies the type of the panel as 'carousel'.
   */
  type: 'carousel';

  /**
   * Optional. Specifies the order of the content in the panel.
   * It can be 'default' or 'reverse'. Defaults to 'default' if not specified.
   */
  order?: 'default' | 'reverse';

  /**
   * Optional. An array of PanelContent objects.
   * Each object represents a slide in the carousel.
   */
  items?: PanelContent[];
}
export type PanelProps = DefaultPanelProps | CarouselPanelProps;

export const Panel = (props: PanelProps) => {

  const [currentImage, setCurrentImage] = React.useState(0);
  const isLargeScreen = useMediaMatch(BREAKPOINTS.LARGE.replace('@media ', ''));

  let rest: React.HTMLAttributes<HTMLDivElement> = {};
  let order = undefined;
  let media = undefined;
  let title = '';
  let content = undefined;
  let items: PanelContent[] = [];


  switch (props.type) {
    case 'carousel':
      ({items = [], order, ...rest} = props);
      break;
    case 'default':
    default:
      ({media, content, title = '', order, ...rest} = props)
      break;
  }


  const moveLeft = () => {
    props.type === 'carousel' && setCurrentImage(currentImage === 0 ? items.length - 1 : currentImage - 1);
  }
  const moveRight = () => {
    props.type === 'carousel' && setCurrentImage(currentImage + 1 > items.length - 1 ? 0 : currentImage + 1);
  }

  const currentItem: PanelContent = props.type === 'carousel' ? items[currentImage] : {media, title, content};

  return (
    <Wrapper {...rest} className={`${rest.className || ''} ${order || ''} `}>
      <Media>
        {
          currentItem.media?.type === 'image' &&
            <img className={currentItem.media.fit} src={currentItem.media.src} alt={currentItem.media.alt}/>
        }
        {
          currentItem.media?.type === 'custom' && currentItem.media.content
        }
      </Media>
      <Body>
        <Quote>{currentItem.title}</Quote>
        <p>{currentItem.content}</p>
      </Body>
      {
        props.type === 'carousel' &&
          <Footer>
              <IconButton action={moveLeft} variant={'secondary'} shape={'circular'}>
                  <SystemIcons.ChevronLeft/>
              </IconButton>
              <LinearProgress size={isLargeScreen ? Size.Large : Size.Medium}
                              className={'progress'}
                              value={currentImage + 1}
                              max={props.items!.length }
                              type={LinearProgressType.Dots}/>
              <IconButton action={moveRight} variant={'secondary'} shape={'circular'}>
                  <SystemIcons.ChevronRight/>
              </IconButton>
          </Footer>
      }
    </Wrapper>
  )
};
